import { MAX_TRIAL_DURATION_DAYS, MIN_TRIAL_DURATION_DAYS } from '@atsready/shared';

/**
 * Everything the trial-policy card needs that is not a React element.
 *
 * It lives here rather than in `TrialPolicyCard.tsx` because of the lint budget:
 * `react-refresh/only-export-components` is a warning with `allowConstantExport`, so a `.tsx`
 * may export a `const` for free and costs one warning per exported **function** — and
 * `package.json` runs `--max-warnings 14` with 13 already spent. One slot, and the card needs
 * more than one function.
 *
 * That is a lint constraint deciding a file boundary, which is not a good reason for a
 * boundary. Recorded because the next reader will wonder: the honest split would put the
 * verdict and the formatter here and the copy in a sibling `trial-policy-copy.ts`, which
 * costs nothing and gives the card's strings one home instead of two. Available, not taken —
 * a fifth file may reasonably be judged worse than one module with two jobs.
 */

// ---------------------------------------------------------------------------
// The copy, all of it approved at gate 1 and none of it templated
// ---------------------------------------------------------------------------

/** The owner's first constraint, rendered. Shown for every role that reaches the card. */
export const P1_TRIALS_DO_NOT_MOVE =
  'Trials already running do not move. Every trial is given an end date when the account is created, and that date is what the product reads — so this changes new accounts only.';

/** The owner's fourth constraint, rendered. Suppressed only below ADMIN. */
export const P2_NOTHING_REACHES_STRIPE =
  'Nothing here is sent to Stripe. The granted trial has no Stripe subscription behind it, and saving this writes one row and one audit record.';

/**
 * **A stage-5 deviation from the copy gate 1 approved, and it is named as one at gate 2.**
 *
 * The approved sentence promised that *"the public pricing page, the FAQ and their search
 * descriptions follow at the marketing site's next build."* They do not. Every deployed
 * marketing build reads a committed snapshot, because `BUILD_API_KEY` exists at no scope —
 * and creating it would not have been enough, since both build steps carry no `env:` block to
 * pass it through. So an operator setting one day would have been told the public site would
 * stop advertising fourteen, and it would have gone on advertising fourteen indefinitely.
 *
 * The transport is closed in code, so this deviation has a defined end: once the owner creates
 * one secret and one variable on the marketing repository, this returns to the approved
 * wording and the change is one string.
 *
 * **First clause tightened 2026-09-11, an hour after the feature shipped**, because it was
 * misread in exactly the way it invited. It said *"The product and this panel show the new length
 * immediately"*, which is true and reads as *"everything now says 30"*. It does not: every account
 * that already has a trial keeps the span it was given, which is P1 two lines above — and the
 * operator who changed the setting went and looked at one. *"Accounts created after you save"*
 * names the population the sentence is actually about, so the two sentences say the same thing
 * twice rather than appearing to disagree.
 */
export const P3_WHERE_THE_NUMBER_SHOWS =
  'Accounts created after you save get the new length; this panel updates immediately. The public marketing site states the trial length separately and does not follow this setting yet, so its pages change on their own deploy and not on this save.';

/** Shown only when the pending value is exactly 1. A stage-5 addition, also named at gate 2. */
export const S8_DAY_ONE_WARNING =
  'At one day, the public marketing pages will read "1 days" in all six languages until that copy is rewritten. This setting is still valid — the minimum is one day.';

/** What a SUPPORT or ANALYST operator is told instead of the value. */
export const BELOW_ADMIN_REFUSAL =
  'Only an administrator can see or change the trial length. Your role is support.';

/** Spoken when the value arrives, so a screen reader is told what landed. */
export function loadedAnnouncement(days: number): string {
  return `Trial policy loaded. Currently ${formatDays(days)}.`;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/**
 * `1 day`, `14 days`. **Every rendered duration in the approved deck takes a singular at 1.**
 *
 * The minimum is one day, so an operator reaches this with a single keystroke — which makes
 * the plural a correctness question rather than a polish one. `admin` has no i18n, so this is
 * the whole mechanism; `app` gets the same result from i18next plural forms, and `marketing`
 * cannot get it at all, which is why its copy is written number-free.
 */
export function formatDays(days: number): string {
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

// ---------------------------------------------------------------------------
// The verdict
// ---------------------------------------------------------------------------

/** What the typed day-count is, and the message it earns. */
export type TrialDaysVerdict =
  | { readonly kind: 'ok'; readonly days: number }
  | { readonly kind: 'empty' | 'not-integer' | 'below-min' | 'above-max'; readonly message: string };

/**
 * One ordered verdict for a typed value, and **the single source of both the message and the
 * preview**.
 *
 * The order is the whole design. Four conditions can hold at once — an empty field is also
 * not a number, `0.5` is both a fraction and below the minimum — and the approved deck gives
 * each its own sentence, so the first matching condition wins and the ladder is fixed here
 * rather than reconstructed at the call site. This package is criticised elsewhere for
 * collapsing four conditions into one message; this is that defect's opposite, deliberately.
 *
 * Called from the card on every keystroke, so the message and the preview cannot disagree by
 * construction. That is stronger than the plan's original shape, which rendered messages from
 * the form schema and would have shown none until the operator pressed Save.
 */
export function judgeTrialDays(raw: string): TrialDaysVerdict {
  const trimmed = raw.trim();
  if (trimmed === '') return { kind: 'empty', message: 'Enter a number of days.' };

  const value = Number(trimmed);
  // `Number.isInteger` and not `% 1`: `Number('abc')` is `NaN`, and `NaN % 1` is `NaN`, which
  // is falsy — so a `% 1` check calls "abc" a whole number.
  if (!Number.isInteger(value)) return { kind: 'not-integer', message: 'Whole days only.' };

  if (value < MIN_TRIAL_DURATION_DAYS) {
    return { kind: 'below-min', message: 'A trial has to last at least one day.' };
  }
  if (value > MAX_TRIAL_DURATION_DAYS) {
    // The message names the bound, and it is the approved string — so it spells 90 rather than
    // interpolating `MAX_TRIAL_DURATION_DAYS`. Gate 1 froze it, which means raising the
    // maximum leaves this sentence stating a false bound. A test pins the string to the
    // constant so that raising one turns the other red.
    return { kind: 'above-max', message: '90 days is the most this can be set to.' };
  }
  return { kind: 'ok', days: value };
}

/** The reason's verdict. Separate, because the two fields refuse independently. */
export function judgeReason(raw: string): { readonly ok: boolean; readonly message: string } {
  return raw.trim() === ''
    ? { ok: false, message: 'Say why you are changing it. This is written to the audit log.' }
    : { ok: true, message: '' };
}

// ---------------------------------------------------------------------------
// The preview and the provenance line
// ---------------------------------------------------------------------------

/** `14 days → 21 days. Every account created after you save gets 21 days.` */
export function previewSentence(current: number, pending: number): string {
  return `${formatDays(current)} → ${formatDays(pending)}. Every account created after you save gets ${formatDays(pending)}.`;
}

/**
 * `Last changed by … on … — "…".`, or the never-changed sentence.
 *
 * `null` covers two situations that collapse to one predicate — no settings row, or a row with
 * no actor — and nothing downstream may tell them apart. Falls back to the email when the
 * actor has no name, because `users.email` is NOT NULL and `users.name` is not, so an empty
 * attribution is reachable and must never render.
 */
export function provenanceSentence(
  lastChange: { at: string; byName: string | null; byEmail: string; reason: string } | null,
): string {
  if (lastChange === null) return 'Never changed. This is the built-in default.';
  const who = lastChange.byName ?? lastChange.byEmail;
  const when = new Date(lastChange.at).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  // The reason can be empty — a seeded row, or an audit row older than the settings row it
  // would describe, which is dropped rather than misattributed. The sentence has to hold
  // together without it.
  return lastChange.reason === ''
    ? `Last changed by ${who} on ${when}.`
    : `Last changed by ${who} on ${when} — “${lastChange.reason}”.`;
}
